<template>
  <div
    class="!flex items-center justify-center cursor-default overflow-y-auto"
    dir="ltr"
    @click.stop.prevent
  >
    <div class="container w-full my-auto py-20 max-w-screen-md space-y-4">
      <div class="tile">
        <div class="font-bold mb-4">
          {{ $t("components.reader.endCard.upNext") }}
        </div>
        <Skeleton v-if="nextPending || !title"></Skeleton>
        <ChapterCard
          v-else-if="next"
          class="cursor-pointer"
          :chapter="next"
          :fallback-url="getCoverUrl(title, '256')"
          no-link
          no-lang
          @click.stop="emit('nextChapter')"
          :chapter-kind="title.attributes.chapterKind || undefined"
          :volume-kind="title.attributes.volumeKind || undefined"
        >
          <template #contextMenu><div></div></template>
        </ChapterCard>
      </div>
      <div>
        <NamiButton
          button-type="primary"
          @buttonClick="emit('nextChapter')"
          block
          >{{ $t("components.reader.endCard.nextChapter") }}
        </NamiButton>
      </div>
      <div class="tile space-y-2">
        <div class="font-bold">Share Your Love</div>
        <div>
          {{ $t("components.reader.endCard.finishedChapterMessage") }}
        </div>
        <Skeleton v-if="currPending || !title" />
        <Reader2InterludeShareBlock
          v-else-if="curr"
          :title="title"
          :chapter="curr"
        />
      </div>
      <div class="space-y-2 tile">
        <div class="font-bold">
          {{ $t("components.reader.endCard.topComments") }}
        </div>
        <FeedCommentPreview feed-type="chapter" :feed-id="page.chapterId" />
        <div class="p-2">
          <NamiButton
            button-type="secondary"
            @buttonClick="emit('openComments')"
            block
            text
            >{{ $t("components.reader.endCard.allComments") }}
          </NamiButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { InterludePageGroup } from "~/src/reader/managers/pageGroup";
import { getReaderChapter } from "~/src/reader/managers/chapter";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { useTitleGet } from "~/utils/tanstack/title";
import { isTitle, type TitleEntity } from "~/src/api";
import { getSingleEntityFromCache } from "~/utils/tanstack";

interface Props {
  page: InterludePageGroup;
}

interface Emits {
  (e: "nextChapter"): void;

  (e: "openComments"): void;
}

const queryClient = useQueryClient();
const cache = queryClient.getQueryCache();
const { page } = defineProps<Props>();
const emit = defineEmits<Emits>();
const { getCoverUrl } = useMediaLink();

const currId = computed(() => page.chapterId);
const nextId = computed(() => page.nextChapterId ?? "");

const { data: curr, isPending: currPending } = getReaderChapter(
  () => queryClient,
  currId,
);
const { data: next, isPending: nextPending } = getReaderChapter(
  () => queryClient,
  nextId,
);

const fetchedTitleId = computed(
  () => curr.value?.relationships.find(isTitle)?.id ?? "",
);

const { data: title } = useQuery({
  ...useTitleGet(fetchedTitleId, ["cover_art", "organization", "tag"]),
  placeholderData: () => {
    if (!fetchedTitleId.value) return;
    return getSingleEntityFromCache(
      cache,
      "title",
      fetchedTitleId.value,
    ) as TitleEntity;
  },
  enabled: computed(() => !!fetchedTitleId.value),
});
</script>

<style scoped lang="postcss"></style>
